import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Modal, Button } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import TableDataLayer from '../TableDataLayer';
import EscalatedReportModal from './EscalatedReportModal';
import ForestHierarchyTree from './ForestHierarchyTree';
import ForestHierarchyGraph from './ForestHierarchyGraph';
import StatusDivisionGraphModal from './StatusDivisionGraphModal';
import StatusReportModal from './StatusReportModal';
import { useNavigate } from 'react-router-dom';

// Fixed zone palette — matches the Claude design (orange / blue / green).
const ZONE_COLORS = { Northern: '#f59e0b', Central: '#3b82f6', Southern: '#22c55e' };

const UnitCountOne = () => {
    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const navigate = useNavigate();
    const [data, setData] = useState({
        total: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        open: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        inProgress: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        close: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        escalated: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        reOpen: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        cancel: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
        resolved: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
    });
    const [loading, setLoading] = useState(true);
    const [selectedStatusCard, setSelectedStatusCard] = useState(null);
    const [showEscalatedReport, setShowEscalatedReport] = useState(false);
    const [reportCard, setReportCard] = useState(null);
    const [escLevels, setEscLevels] = useState({ DCF: 0, Conservator: 0, Chief: 0 });
    // Count of informative calls (informative:true), for the Informative Calls card.
    const [informativeCount, setInformativeCount] = useState(0);
    const [showHierarchy, setShowHierarchy] = useState(false);
    const [showGraph, setShowGraph] = useState(false);
    const [graphCard, setGraphCard] = useState(null);

    const openStatusComplaints = (card) => setSelectedStatusCard(card);
    const closeStatusComplaints = () => setSelectedStatusCard(null);

    // Report button. Every status card opens its own formatted PDF report,
    // grouped by assigned officer, same as the Escalated report.
    const handleReport = (card) => {
        if (card.statusValue === 'Escalated') {
            setShowEscalatedReport(true);
        } else {
            setReportCard(card);
        }
    };

    // Load the Space Grotesk numerals used by the Claude design (falls back to
    // the theme font if the request fails — nothing breaks).
    useEffect(() => {
        const id = 'space-grotesk-font';
        if (!document.getElementById(id)) {
            const link = document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&display=swap';
            document.head.appendChild(link);
        }
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const statusResponse = await fetch(`${API_BASE_URL}/backend/complaints/complaints-status-count`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            const zoneResponse = await fetch(`${API_BASE_URL}/backend/complaints/complaints-status-count-zone-wise`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            if (statusResponse.ok && zoneResponse.ok) {
                const statusResult = await statusResponse.json();
                const zoneResult = await zoneResponse.json();

                if (statusResult.status && zoneResult.status) {
                    const counts = {
                        total: { count: statusResult.totalCount, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        open: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        inProgress: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        close: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        escalated: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        reOpen: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        cancel: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                        resolved: { count: 0, zones: { Northern: 0, Central: 0, Southern: 0 } },
                    };

                    statusResult.data.forEach(item => {
                        if (item.status === "Open") counts.open.count = item.count;
                        else if (item.status === "In Progress") counts.inProgress.count = item.count;
                        else if (item.status === "Closed") counts.close.count = item.count;
                        else if (item.status === "Escalated") counts.escalated.count = item.count;
                        else if (item.status === "Reopened") counts.reOpen.count = item.count;
                        else if (item.status === "Cancelled") counts.cancel.count = item.count;
                        else if (item.status === "Resolved") counts.resolved.count = item.count;
                    });

                    zoneResult.data.forEach(item => {
                        const statusKey = item.status.toLowerCase() === 'total' ? 'total' :
                                         item.status.toLowerCase() === 'open' ? 'open' :
                                         item.status.toLowerCase() === 'in progress' ? 'inProgress' :
                                         item.status.toLowerCase() === 'closed' ? 'close' :
                                         item.status.toLowerCase() === 'escalated' ? 'escalated' :
                                         item.status.toLowerCase() === 'reopened' ? 'reOpen' :
                                         item.status.toLowerCase() === 'cancelled' ? 'cancel' :
                                         item.status.toLowerCase() === 'resolved' ? 'resolved' : null;
                        if (statusKey) {
                            item.zones.forEach(zone => {
                                // Open complaints are not yet dispatched, so they carry no zone.
                                counts[statusKey].zones[zone.name] = statusKey === 'open' ? 0 : zone.count;
                            });
                        }
                    });

                    setData(counts);
                }
            } else {
                console.error('Failed to fetch data:', statusResponse.statusText, zoneResponse.statusText);
            }

            // Escalation level breakdown for the Escalated card.
            try {
                const lvlRes = await fetch(`${API_BASE_URL}/backend/complaints/escalated-count-by-level`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (lvlRes.ok) {
                    const lvlJson = await lvlRes.json();
                    if (lvlJson.status && lvlJson.data) setEscLevels(lvlJson.data);
                }
            } catch (lvlErr) {
                console.warn('Escalation level breakdown failed:', lvlErr);
            }

            // Informative calls count (informative:true). Reuses the scoped
            // status-count endpoint with informative=true; its totalCount is the
            // number of informative calls.
            try {
                const infRes = await fetch(`${API_BASE_URL}/backend/complaints/complaints-status-count-scoped?informative=true`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (infRes.ok) {
                    const infJson = await infRes.json();
                    if (infJson.status) setInformativeCount(infJson.totalCount || 0);
                }
            } catch (infErr) {
                console.warn('Informative calls count failed:', infErr);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const statusCards = [
        {
            title: "Total", value: data.total.count, zones: data.total.zones,
            statusValue: "Total", icon: "mdi:file-document-multiple-outline", accent: "#6366f1",
            description: "",
        },
        {
            title: "Open", value: data.open.count, zones: data.open.zones,
            statusValue: "Open", icon: "mdi:bullhorn-outline", accent: "#0F6674",
            description: "Complaints lodged in the control room, ready to dispatch and assign to a field officer or admin staff by the call-room operator.",
        },
        {
            // Renamed from "In Progress" -> "Assigned" (status value kept for the API filter).
            title: "Assigned", value: data.inProgress.count, zones: data.inProgress.zones,
            statusValue: "In Progress", icon: "mdi:account-arrow-right-outline", accent: "#7C3AED",
            description: "Complaints assigned to field staff or administration by the CRO.",
        },
        {
            title: "Reply Submitted", value: data.close.count, zones: data.close.zones,
            statusValue: "Closed", icon: "mdi:clipboard-check-outline", accent: "#3B82F6",
            description: "Reply submitted by field or admin staff; complainant feedback is pending — the CRO will confirm and move it to Resolved.",
        },
        {
            title: "Escalated", value: data.escalated.count, zones: data.escalated.zones,
            statusValue: "Escalated", icon: "mdi:alert-octagon-outline", accent: "#DC2626",
            description: "Assigned to field staff with 3+ days elapsed and still not resolved.",
        },
        {
            title: "Re-Open", value: data.reOpen.count, zones: data.reOpen.zones,
            statusValue: "Reopened", icon: "mdi:refresh", accent: "#D97706",
            description: "Re-opened when the complainant is not satisfied, based on their feedback.",
        },
        // "Dropped" (Cancelled) status retired — dropped complaints are Closed now.
        {
            title: "Resolved", value: data.resolved.count, zones: data.resolved.zones,
            statusValue: "Resolved", icon: "mdi:check-decagram-outline", accent: "#16A34A",
            description: "Feedback received from the complainant and the issue resolved and closed by field or administration.",
        },
    ];

    // Two special (non-status) cards shown right AFTER the Re-Open card:
    //  • Informative Calls — a special category judged on the `informative` flag,
    //    NOT a complaint status. It never enters the status lifecycle (recorded
    //    only, simply closed). Count comes from the informative column; the card
    //    links to the existing /informative-calls page.
    //  • Under Inquiry — a status to be added later; hardcoded to 00 for now.
    const afterReopenCards = [
        {
            title: "Informative Calls",
            value: informativeCount || 0,
            icon: "mdi:phone-in-talk-outline",
            accent: "#0d9488",
            badge: "Special category",
            description: "Judged on the informative flag — not a complaint status. Recorded only and simply closed; never part of the status lifecycle.",
            action: { label: "View", onClick: () => navigate('/informative-calls') },
        },
        {
            title: "Under Inquiry",
            value: "00",
            icon: "mdi:magnify-scan",
            accent: "#6b7280",
            badge: "Coming soon",
            description: "New status — hardcoded to 00 for now; will be wired up once its flow is integrated into complaint handling.",
        },
    ];

    const total = data.total.count || 0;
    const pctOf = (v) => (total > 0 ? ((v / total) * 100).toFixed(1) : '0.0');

    const renderCard = (card, index) => {
        const isTotal = card.statusValue === 'Total';
        const isOpen = card.statusValue === 'Open';
        const isEscalated = card.statusValue === 'Escalated';
        const zoneSum = card.zones.Northern + card.zones.Central + card.zones.Southern;
        const seg = (v) => (zoneSum > 0 ? `${(v / zoneSum) * 100}%` : '0%');

        return (
            <div className="col" key={index}>
                <div
                    className={`card h-100 uc-card${isTotal ? ' uc-card--total' : ''}`}
                    style={{ '--acc': card.accent }}
                    onClick={() => openStatusComplaints(card)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openStatusComplaints(card); }
                    }}
                    role="button"
                    tabIndex={0}
                    title={`Click to view ${card.title.toLowerCase()} complaints`}
                >
                    <div className="card-body p-20 d-flex flex-column h-100">
                        {/* Chip + icon */}
                        <div className="d-flex align-items-start justify-content-between">
                            <span className="uc-chip">{card.title}</span>
                            <span className="uc-iconbox"><Icon icon={card.icon} /></span>
                        </div>

                        {/* Number + % of total */}
                        <div className="d-flex align-items-end flex-wrap gap-2 mt-2">
                            <span className="uc-number">{card.value}</span>
                            {!isTotal && <span className="uc-pct">{pctOf(card.value)}% of total</span>}
                        </div>

                        {/* Description */}
                        {card.description && (
                            <p className="uc-desc mt-2 mb-0">{card.description}</p>
                        )}

                        {/* Body block: Escalated shows a level breakdown (by officer
                            rank); Open has no zone yet; all others show the zone
                            distribution. All three occupy a similar vertical footprint
                            so every card keeps the same height. */}
                        {isEscalated ? (
                            <div className="mt-3">
                                <div className="uc-distlabel mb-1">Escalation by authority</div>
                                <div className="uc-levels">
                                    {[
                                        { key: 'DCF', code: 'DCF', name: 'Deputy Conservator of Forest', dot: '#fca5a5' },
                                        { key: 'Conservator', code: 'CF', name: 'Conservator of Forest', dot: '#f87171' },
                                        { key: 'Chief', code: 'CCF', name: 'Chief Conservator of Forest', dot: '#ef4444', super: true },
                                    ].map((lvl) => (
                                        <div
                                            className={`uc-lvl${lvl.super ? ' uc-lvl-super' : ''}`}
                                            key={lvl.key}
                                            title={`${lvl.name} (${lvl.code}): ${escLevels[lvl.key] || 0}`}
                                        >
                                            <span className="uc-lvl-dot" style={{ background: lvl.dot }} />
                                            <span className="uc-lvl-main">
                                                <span className="uc-lvl-name">{lvl.name}</span>
                                                {lvl.super ? (
                                                    <span className="uc-lvl-tag"><Icon icon="mdi:fire" /> Super Escalated · {lvl.code}</span>
                                                ) : (
                                                    <span className="uc-lvl-code">{lvl.code}</span>
                                                )}
                                            </span>
                                            <span className="uc-lvl-count">{escLevels[lvl.key] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : isOpen ? (
                            <div className="uc-nozone mt-3">
                                <Icon icon="mdi:map-marker-off-outline" />
                                <span>Zone assigned once dispatched</span>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <div className="uc-distlabel mb-1">Distribution by zone</div>
                                <div className="uc-bar">
                                    {zoneSum > 0 ? (
                                        <>
                                            <span style={{ width: seg(card.zones.Northern), background: ZONE_COLORS.Northern }} />
                                            <span style={{ width: seg(card.zones.Central), background: ZONE_COLORS.Central }} />
                                            <span style={{ width: seg(card.zones.Southern), background: ZONE_COLORS.Southern }} />
                                        </>
                                    ) : (
                                        <span className="uc-bar-empty" />
                                    )}
                                </div>
                                <div className="uc-zones mt-2">
                                    {['Northern', 'Central', 'Southern'].map((z) => (
                                        <div className="uc-zone" key={z}>
                                            <span className="uc-dot" style={{ background: ZONE_COLORS[z] }} />
                                            <span className="uc-zone-name">{z}</span>
                                            <span className="uc-zone-count">{card.zones[z]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Actions — every card except Total. Buttons are disabled
                            when the count is 0; Open has no Report/Graph because it
                            isn't dispatched to a division yet. */}
                        {!isTotal && (() => {
                            const noData = !card.value;
                            return (
                                <div className="uc-actions mt-auto pt-3">
                                    <button
                                        type="button"
                                        className="uc-btn uc-btn-view"
                                        disabled={noData}
                                        onClick={(e) => { e.stopPropagation(); if (!noData) openStatusComplaints(card); }}
                                        title={noData ? `No ${card.title.toLowerCase()} complaints` : `View ${card.title} complaints`}
                                    >
                                        <Icon icon="mdi:eye-outline" /> View
                                    </button>
                                    {!isOpen && (
                                        <button
                                            type="button"
                                            className="uc-btn uc-btn-report"
                                            disabled={noData}
                                            onClick={(e) => { e.stopPropagation(); if (!noData) handleReport(card); }}
                                            title={noData ? `No ${card.title.toLowerCase()} complaints`
                                                : card.statusValue === 'Escalated'
                                                    ? 'Open the escalated complaints PDF report'
                                                    : `Open the ${card.title} complaints report`}
                                        >
                                            <span className="uc-pdf">PDF</span> Report
                                        </button>
                                    )}
                                    {!isOpen && (
                                        <button
                                            type="button"
                                            className="uc-btn uc-btn-report"
                                            disabled={noData}
                                            onClick={(e) => { e.stopPropagation(); if (!noData) setGraphCard(card); }}
                                            title={noData ? `No ${card.title.toLowerCase()} complaints` : `View ${card.title} complaints by division as a graph`}
                                        >
                                            <Icon icon="mdi:chart-bar" /> Graph
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    // Lightweight card for the extra (non-status) tiles: chip + icon + number +
    // description, and an optional single action button. No zone distribution
    // (these figures are not zone-scoped).
    const renderInfoCard = (card, index) => {
        const clickable = Boolean(card.action);
        return (
            <div className="col" key={`extra-${index}`}>
                <div
                    className="card h-100 uc-card"
                    style={{ '--acc': card.accent }}
                    onClick={clickable ? card.action.onClick : undefined}
                    onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.action.onClick(); } } : undefined}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    title={clickable ? `View ${card.title.toLowerCase()}` : card.title}
                >
                    <div className="card-body p-20 d-flex flex-column h-100">
                        <div className="d-flex align-items-start justify-content-between">
                            <span className="uc-chip">{card.title}</span>
                            <span className="uc-iconbox"><Icon icon={card.icon} /></span>
                        </div>
                        <div className="d-flex align-items-end flex-wrap gap-2 mt-2">
                            <span className="uc-number">{card.value}</span>
                            {card.badge && <span className="uc-pct">{card.badge}</span>}
                        </div>
                        {card.description && (
                            <p className="uc-desc mt-2 mb-0">{card.description}</p>
                        )}
                        {card.action && (
                            <div className="uc-actions mt-auto pt-3">
                                <button
                                    type="button"
                                    className="uc-btn uc-btn-view"
                                    onClick={(e) => { e.stopPropagation(); card.action.onClick(); }}
                                    title={`View ${card.title.toLowerCase()}`}
                                >
                                    <Icon icon="mdi:eye-outline" /> {card.action.label}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Full-width Total card: horizontal layout (icon + number on the left, the
    // zone distribution filling the rest) so it spans the top at a normal height.
    const renderTotalCard = (card) => {
        const zoneSum = card.zones.Northern + card.zones.Central + card.zones.Southern;
        const seg = (v) => (zoneSum > 0 ? `${(v / zoneSum) * 100}%` : '0%');
        return (
            <div className="col-12">
                <div
                    className="card uc-card uc-card--total uc-card--wide"
                    style={{ '--acc': card.accent }}
                    onClick={() => openStatusComplaints(card)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openStatusComplaints(card); } }}
                    role="button"
                    tabIndex={0}
                    title="Click to view all complaints"
                >
                    <div className="card-body p-20">
                        <div className="d-flex align-items-center flex-wrap gap-4">
                            <div className="d-flex align-items-center gap-3">
                                <span className="uc-iconbox uc-iconbox--lg"><Icon icon={card.icon} /></span>
                                <div>
                                    <span className="uc-chip">{card.title}</span>
                                    <div className="uc-number uc-number--xl mt-1">{card.value}</div>
                                </div>
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 240 }}>
                                <div className="uc-distlabel mb-1">Distribution by zone</div>
                                <div className="uc-bar">
                                    {zoneSum > 0 ? (
                                        <>
                                            <span style={{ width: seg(card.zones.Northern), background: ZONE_COLORS.Northern }} />
                                            <span style={{ width: seg(card.zones.Central), background: ZONE_COLORS.Central }} />
                                            <span style={{ width: seg(card.zones.Southern), background: ZONE_COLORS.Southern }} />
                                        </>
                                    ) : (
                                        <span className="uc-bar-empty" />
                                    )}
                                </div>
                                <div className="uc-zones mt-2">
                                    {['Northern', 'Central', 'Southern'].map((z) => (
                                        <div className="uc-zone" key={z}>
                                            <span className="uc-dot" style={{ background: ZONE_COLORS[z] }} />
                                            <span className="uc-zone-name">{z}</span>
                                            <span className="uc-zone-count">{card.zones[z]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Hierarchical (Zone → Circle → Division) overview — expands inline */}
                            <button
                                type="button"
                                className="uc-btn uc-btn-view uc-hier-btn"
                                onClick={(e) => { e.stopPropagation(); setShowHierarchy((v) => !v); setShowGraph(false); }}
                                title="Toggle the forest hierarchy overview"
                            >
                                <Icon icon={showHierarchy ? 'mdi:chevron-up' : 'mdi:file-tree-outline'} />
                                {showHierarchy ? 'Hide Hierarchy' : 'Hierarchical Overview'}
                            </button>
                            <button
                                type="button"
                                className="uc-btn uc-btn-report uc-hier-btn"
                                onClick={(e) => { e.stopPropagation(); setShowGraph((v) => !v); setShowHierarchy(false); }}
                                title="View the hierarchy as an interactive graph"
                            >
                                <Icon icon={showGraph ? 'mdi:chevron-up' : 'mdi:chart-tree'} />
                                {showGraph ? 'Hide Graph' : 'Graphical Trend'}
                            </button>
                        </div>

                        {/* Inline hierarchy panel — clicks inside must not trigger the
                            card's own "open Total list" handler. */}
                        {showHierarchy && (
                            <div
                                className="mt-3 pt-3 border-top"
                                onClick={(e) => e.stopPropagation()}
                                role="presentation"
                            >
                                <div className="uc-distlabel mb-2">Complaints by Zone → Circle → Division</div>
                                <ForestHierarchyTree />
                            </div>
                        )}

                        {/* Inline interactive graph (treemap drill-down) */}
                        {showGraph && (
                            <div
                                className="mt-3 pt-3 border-top"
                                onClick={(e) => e.stopPropagation()}
                                role="presentation"
                            >
                                <div className="uc-distlabel mb-2">Graphical trend - drill Zone to Circle to Division, then view status cards</div>
                                <ForestHierarchyGraph />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const totalCard = statusCards.find((c) => c.statusValue === 'Total');
    const otherCards = statusCards.filter((c) => c.statusValue !== 'Total');

    // Interleave the special cards into the status sequence: Informative Calls +
    // Under Inquiry right after "Re-Open". (Escalation authority levels already
    // show inside the Escalated card, so no separate CF/CCF cards here.)
    const dashboardTiles = [];
    otherCards.forEach((card) => {
        dashboardTiles.push({ type: 'status', card });
        if (card.statusValue === 'Reopened') afterReopenCards.forEach((c) => dashboardTiles.push({ type: 'info', card: c }));
    });

    return (
        <>
            <style>{`
                .uc-card {
                    position: relative;
                    overflow: hidden;
                    border-radius: 18px;
                    border: 1px solid color-mix(in srgb, var(--acc) 22%, transparent);
                    cursor: pointer;
                    transition: transform .18s ease, box-shadow .18s ease;
                }
                .uc-card::before {
                    content: '';
                    position: absolute; inset: 0;
                    background: linear-gradient(135deg, color-mix(in srgb, var(--acc) 13%, transparent) 0%, transparent 55%);
                    pointer-events: none;
                }
                .uc-card:hover { transform: translateY(-4px); box-shadow: 0 14px 30px color-mix(in srgb, var(--acc) 24%, transparent); }
                .uc-card > .card-body { position: relative; z-index: 1; }
                .uc-chip {
                    display: inline-block; font-size: .82rem; font-weight: 800; letter-spacing: .06em;
                    text-transform: uppercase; padding: .34rem .7rem; border-radius: 999px;
                    color: var(--acc); background: color-mix(in srgb, var(--acc) 15%, transparent);
                }
                .uc-iconbox {
                    width: 44px; height: 44px; border-radius: 13px; font-size: 1.4rem;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--acc); background: color-mix(in srgb, var(--acc) 14%, transparent);
                }
                .uc-number {
                    font-family: 'Space Grotesk', 'Segoe UI', system-ui, sans-serif;
                    font-weight: 700; font-size: 2.5rem; line-height: 1; letter-spacing: -1.5px;
                    font-variant-numeric: tabular-nums; color: var(--uc-num, #0f172a);
                }
                .uc-card--total .uc-number { font-size: 3rem; font-weight: 700; }
                .uc-number--xl { font-size: 3.4rem !important; }
                .uc-iconbox--lg { width: 56px; height: 56px; font-size: 1.85rem; border-radius: 16px; }
                .uc-card--wide .uc-zones { max-width: 520px; }
                .uc-hier-btn { flex: 0 0 auto; padding: .55rem 1rem; white-space: nowrap; align-self: center; }
                .uc-pct {
                    font-size: .7rem; font-weight: 600; color: #64748b;
                    background: rgba(100,116,139,.14); padding: .18rem .55rem; border-radius: 999px; margin-bottom: .3rem;
                }
                .uc-desc { font-size: .74rem; line-height: 1.4; color: #64748b; }
                .uc-distlabel { font-size: .6rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #94a3b8; }
                .uc-bar { display: flex; height: 8px; border-radius: 999px; overflow: hidden; background: color-mix(in srgb, var(--acc) 12%, #eef2f6); }
                .uc-bar > span { display: block; height: 100%; }
                .uc-bar .uc-bar-empty { width: 100%; background: color-mix(in srgb, var(--acc) 12%, #e2e8f0); }
                .uc-zones { display: flex; gap: .5rem; }
                .uc-zone { flex: 1; display: flex; align-items: center; gap: .28rem; font-size: .68rem; min-width: 0; }
                .uc-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
                .uc-zone-name { color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .uc-zone-count { margin-left: auto; font-weight: 700; color: var(--uc-num, #0f172a); }
                /* Escalation-by-authority breakdown (Escalated card). Sized to match
                   the zone-distribution block so all cards keep the same height. */
                .uc-levels { display: flex; flex-direction: column; gap: .3rem; }
                .uc-lvl { display: flex; align-items: center; gap: .5rem; }
                .uc-lvl-dot { width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }
                .uc-lvl-main { display: flex; flex-direction: column; line-height: 1.15; min-width: 0; flex: 1 1 auto; }
                .uc-lvl-name { font-size: .72rem; font-weight: 700; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .uc-lvl-code { font-size: .58rem; font-weight: 700; letter-spacing: .04em; color: #94a3b8; text-transform: uppercase; }
                .uc-lvl-count { margin-left: auto; font-size: .95rem; font-weight: 800; color: var(--uc-num, #0f172a); font-variant-numeric: tabular-nums; }
                /* "Super Escalated" (CCF) row — accent-tinted highlight to signal severity. */
                .uc-lvl-super { background: color-mix(in srgb, var(--acc) 10%, transparent); border: 1px solid color-mix(in srgb, var(--acc) 24%, transparent);
                    border-radius: 10px; padding: .28rem .45rem; margin-top: .05rem; }
                .uc-lvl-super .uc-lvl-name { color: var(--acc); }
                .uc-lvl-super .uc-lvl-count { color: var(--acc); }
                .uc-lvl-tag { display: inline-flex; align-items: center; gap: .18rem; font-size: .56rem; font-weight: 800;
                    letter-spacing: .03em; color: #fff; background: var(--acc); border-radius: 999px; padding: .05rem .34rem; width: fit-content; margin-top: .12rem; text-transform: uppercase; }
                .uc-nozone { display: flex; align-items: center; gap: .4rem; font-size: .72rem; color: #94a3b8;
                    background: color-mix(in srgb, var(--acc) 8%, transparent); border-radius: 10px; padding: .55rem .7rem; }
                .uc-actions { display: flex; gap: .5rem; }
                .uc-btn {
                    flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: .35rem;
                    border-radius: 9px; padding: .3rem .4rem; font-weight: 600; font-size: .8rem; line-height: 1.2;
                    border: 1px solid transparent; transition: transform .15s ease, filter .15s ease, background .15s ease;
                }
                .uc-btn:hover { transform: translateY(-1px); }
                .uc-btn:disabled { opacity: .45; cursor: not-allowed; transform: none !important; filter: grayscale(.3); }
                .uc-btn-view { color: #fff; background: var(--acc); }
                .uc-btn-view:hover { filter: brightness(1.07); }
                .uc-btn-report { background: transparent; color: #475569; border-color: color-mix(in srgb, var(--acc) 28%, #e2e8f0); }
                .uc-btn-report:hover { background: color-mix(in srgb, var(--acc) 8%, transparent); }
                .uc-pdf { font-size: .58rem; font-weight: 800; color: #ef4444; border: 1px solid #ef4444; border-radius: 4px; padding: 0 .2rem; line-height: 1.3; }
                /* Dark theme: keep numerals/counts readable on dark cards. */
                [data-theme="dark"] .uc-card, .dark .uc-card { --uc-num: #f1f5f9; }
            `}</style>

            {loading ? (
                <>
                    <div className="row g-3 mb-3">
                        <div className="col-12">
                            <div className="card border" style={{ borderRadius: '18px' }}>
                                <div className="card-body p-20 d-flex align-items-center flex-wrap gap-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <Skeleton width={56} height={56} borderRadius={16} />
                                        <div>
                                            <Skeleton width={70} height={20} />
                                            <Skeleton width={110} height={48} />
                                        </div>
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 240 }}>
                                        <Skeleton height={8} />
                                        <Skeleton height={16} className="mt-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div className="col" key={index}>
                                <div className="card h-100 border" style={{ borderRadius: '18px' }}>
                                    <div className="card-body p-20">
                                        <div className="d-flex justify-content-between">
                                            <Skeleton width={70} height={22} />
                                            <Skeleton width={44} height={44} borderRadius={13} />
                                        </div>
                                        <Skeleton width={90} height={44} className="mt-2" />
                                        <Skeleton count={2} className="mt-2" />
                                        <Skeleton height={8} className="mt-3" />
                                        <div className="d-flex gap-2 mt-3">
                                            <Skeleton width="100%" height={36} />
                                            <Skeleton width="100%" height={36} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    {/* Total — full width across the top */}
                    <div className="row g-3 mb-3">
                        {totalCard && renderTotalCard(totalCard)}
                    </div>
                    {/* Status cards interleaved with the special cards:
                        …Re-Open → Informative Calls → Under Inquiry → Resolved. */}
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3">
                        {dashboardTiles.map((t, index) => (
                            t.type === 'status' ? renderCard(t.card, index) : renderInfoCard(t.card, index)
                        ))}
                    </div>
                </>
            )}

            <Modal
                show={Boolean(selectedStatusCard)}
                onHide={closeStatusComplaints}
                centered
                size="xl"
                fullscreen="xl-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedStatusCard?.title || 'Complaints'} Complaints
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto' }}>
                    {selectedStatusCard && (
                        <TableDataLayer
                            key={selectedStatusCard.statusValue}
                            initialStatus={selectedStatusCard.statusValue}
                            embedded
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <a
                        className="btn btn-success"
                        href={
                            selectedStatusCard?.statusValue && selectedStatusCard.statusValue !== 'Total'
                                ? `/complains/list?status=${encodeURIComponent(selectedStatusCard.statusValue)}`
                                : '/complains/list'
                        }
                    >
                        Open Full List
                    </a>
                    <Button variant="secondary" onClick={closeStatusComplaints}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <EscalatedReportModal
                show={showEscalatedReport}
                onHide={() => setShowEscalatedReport(false)}
            />
            <StatusReportModal
                show={Boolean(reportCard)}
                onHide={() => setReportCard(null)}
                title={reportCard?.title}
                apiStatus={reportCard?.statusValue}
            />
            <StatusDivisionGraphModal
                show={Boolean(graphCard)}
                onHide={() => setGraphCard(null)}
                status={graphCard?.statusValue}
                title={graphCard?.title}
                color={graphCard?.accent}
            />
        </>
    );
};

export default UnitCountOne;
